import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  cx,
  DropdownItem,
  DropdownMenu,
  IconButton,
  type IconName,
  ScrollEdge,
  SegmentedControl,
} from '../../ui';

/** A group owns the material; its children are borderless controls on that material. */
export function ToolbarGroup({
  children,
  label,
  items,
  navigation = false,
  ref,
}: {
  readonly children: ReactNode;
  readonly label: string;
  /**
   * THE WINDOW'S BACK AND FORWARD GROUP (fulfillment round 2, F2). The kit assembles it as its
   * own group, not as a generic item group: two segment buttons centred at x 19.5 and 53.5 of
   * a 73x36 capsule with a separator between them (`Windows/Light/Unified Toolbar + Title +
   * Sidebar/Active`, Right Pane/Titlebar and Toolbar/Navigation). `index.css` draws that
   * geometry and the kit's symbol inks from this mark.
   */
  readonly navigation?: boolean | undefined;
  /**
   * How many actions the PAGE handed this group, whether or not they are all drawn in it.
   * The bar's own group says it so a review can ask the running product the question the
   * overflow raises - is every action still reachable - instead of counting pixels
   * (`packages/e2e/review/behaviour.spec.ts`, (t3)).
   */
  readonly items?: number | undefined;
  readonly ref?: Ref<HTMLFieldSetElement> | undefined;
}) {
  return (
    <fieldset
      aria-label={label}
      className="shell-toolbar-group"
      ref={ref}
      {...(navigation ? { 'data-toolbar-group': 'navigation' } : {})}
      {...(items === undefined ? {} : { 'data-toolbar-actions': items })}
    >
      {children}
    </fieldset>
  );
}

/**
 * UNDO IS THERE WHEN THERE IS SOMETHING TO UNDO (SH-A12, the orchestrator's finding on
 * owner-03 and on every other toolbar). The shell reserved Undo's place in S1 and shipped
 * the reservation: a disabled capsule stood in the frame of every screen in the product,
 * permanently, for an operation that does not exist yet. A toolbar item that can never do
 * anything is not a placeholder, it is a dead control - so it renders only when a screen
 * hands in an `onUndo`, and until the action layer exists that is nowhere.
 *
 * `specimen` is the one exception and it is the states page: a gallery of shell states has
 * to be able to draw the disabled state of a control on purpose (S1-5 stays OPEN there).
 */
export function ToolbarUndo({
  onUndo,
  label = 'Undo',
  specimen = false,
}: {
  readonly onUndo?: (() => void) | undefined;
  readonly label?: string;
  readonly specimen?: boolean;
}) {
  if (onUndo === undefined && !specimen) return null;
  return (
    <Button variant="ghost" disabled={onUndo === undefined} onClick={onUndo} aria-label={label}>
      {label}
    </Button>
  );
}

function flatten(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((child) =>
    isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment
      ? flatten(child.props.children)
      : [child],
  );
}

/**
 * Existing screen actions keep their handlers. One primary Button leaves the neutral group.
 *
 * AN ACTION THAT IS ALREADY A GROUP STAYS ONE (round 3, finding A6). A screen may hand in a
 * `ToolbarGroup` it composed itself - the dashboard's day control is back, "Today", forward
 * and the shown date, which is one item group in Apple's Calendar toolbar and not four
 * loose items. Wrapping it in the page-actions group would have nested a capsule inside a
 * capsule and swept Customize into it, which is how round 2 ended up with the date field
 * and Customize sharing one long capsule.
 */
/**
 * A MACOS TOOLBAR ITEM IS A SYMBOL, AND THE ITEM DECIDES THAT, NOT THE PAGE (wave 4 round
 * 1, C2-2; owner-77, owner-80, owner-81: "Apple nutzt aber auch gar nicht solche Buttons,
 * so wie New, sondern da ist dann einfach ein Plus umkreist und ebenfalls als Liquid Glass
 * Element", "Blauer Button macht Apple nicht").
 *
 * The kit is unambiguous: searching all 396 masters of `Titlebars and Toolbars` for `label`,
 * `title`, `tint`, `prominent` and `accent` returns ZERO hits, and the only labelled text a
 * window frame carries is the window title and the search placeholder. What it ships is
 * `Titlebars and Toolbars/{Light,Dark}/XL/Buttons/Active/1` - a 36px glass circle with a
 * 28px MONOCHROME symbol inside it - and the blue capsule the product drew cites
 * `Buttons/.../Large/Glass Prominent/Default`, an iOS 83x50 button master that has no place
 * in a Mac window frame.
 *
 * SO THE CONVERSION HAPPENS HERE, IN THE BAR, and the pages keep handing in what they
 * always handed in. An action that CARRIES a symbol becomes that symbol with its words as
 * the help tag; an action that carries none has nothing to draw, so it goes into the bar's
 * More menu until its page passes one. Doing it in the bar rather than in twenty page files
 * is the same argument SH-A1 made about the item box: one construction, in one place, that
 * cannot drift.
 */
interface ToolbarMenuRow {
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled: boolean;
}

type ToolbarSymbolItem =
  | {
      readonly kind: 'item';
      readonly node: ReactNode;
      /**
       * What this item looks like in the More menu, for the items the bar may have to PUSH
       * there when the window is too narrow (wave 5, A2-3). An item that cannot be written
       * as a row - a menu's trigger, a control a page composed itself - carries none and
       * stays in the bar, because burying a list behind a list is the thing C2-2 already
       * refused.
       */
      readonly menu?: ToolbarMenuRow | undefined;
    }
  | ({ readonly kind: 'overflow' } & ToolbarMenuRow);

/** The words an action was handed in with; they become its help tag or its menu row. */
function textOf(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

interface ActionProps {
  readonly icon?: IconName;
  readonly children?: ReactNode;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly 'aria-label'?: string;
  readonly trigger?: ReactNode;
}

/**
 * One item, turned into the master it belongs to. A wrapper that renders a TRIGGER (a menu,
 * a popover, a split button) is asked for its trigger and cloned around the converted one,
 * for the same structural reason `isProminent` looks through it: the toolbar then does not
 * have to import a menu to know what a menu is, and Members' "Add" - a create action handed
 * in as a `DropdownMenu` trigger - is the same plus symbol as every other create action.
 */
function asToolbarSymbolItem(item: ReactNode, key: string): ToolbarSymbolItem {
  if (!isValidElement<ActionProps>(item)) return { kind: 'item', node: item };
  if (item.type === IconButton) {
    const press = item.props.onClick as (() => void) | undefined;
    const name = item.props['aria-label'] ?? (item.props as { label?: string }).label ?? '';
    return {
      kind: 'item',
      node: item,
      ...(name === '' || press === undefined
        ? {}
        : {
            menu: {
              label: name,
              onSelect: () => press(),
              disabled: item.props.disabled === true,
            },
          }),
    };
  }
  if (item.props.trigger !== undefined) {
    const inner = asToolbarSymbolItem(item.props.trigger, `${key}-trigger`);
    if (inner.kind === 'item')
      return { kind: 'item', node: cloneElement(item, { trigger: inner.node }) };
    /* A MENU WHOSE TRIGGER HAS NO SYMBOL STAYS AND SAYS SO. Folding the menu itself into
       More would bury a whole list of actions behind another list, so the wrapper keeps
       rendering and carries the mark check (o) and the states page read: this is a page
       that owes the bar a symbol. */
    return {
      kind: 'item',
      node: (
        <span key={key} data-toolbar-symbol="missing" className="contents">
          {item}
        </span>
      ),
    };
  }
  if (item.type !== Button) return { kind: 'item', node: item };
  const label = item.props['aria-label'] ?? textOf(item.props.children);
  const disabled = item.props.disabled === true || item.props.loading === true;
  const symbol = item.props.icon;
  if (symbol === undefined) {
    /* A MENU ROW HAS NO MOUSE EVENT TO HAND ON, and every action this reaches is a verb
       the page wrote as `() => setCreating(true)`. The handler is invoked with nothing
       rather than with an invented event object, which is what a fabricated
       `React.MouseEvent` would be - a page that really needs the event has to pass a
       symbol and stay in the bar. */
    const onClick = item.props.onClick as (() => void) | undefined;
    return { kind: 'overflow', label, onSelect: () => onClick?.(), disabled };
  }
  const press = item.props.onClick as (() => void) | undefined;
  return {
    kind: 'item',
    node: (
      <IconButton
        key={key}
        icon={symbol}
        label={label}
        data-toolbar-item={label}
        {...(item.props.onClick === undefined ? {} : { onClick: item.props.onClick })}
        {...(disabled ? { disabled: true } : {})}
      />
    ),
    ...(press === undefined ? {} : { menu: { label, onSelect: () => press(), disabled } }),
  };
}

/**
 * HOW MANY OF THE BAR'S OWN ITEMS FIT, MEASURED ON THE BAR (wave 5, A2-3).
 *
 * A Mac toolbar is one row and the items that no longer fit go into an overflow menu at the
 * trailing edge - hig:
 * https://developer.apple.com/design/human-interface-guidelines/toolbars "The system
 * automatically adds an overflow menu in macOS or iPadOS when items no longer fit"; doc:
 * https://developer.apple.com/documentation/appkit/nstoolbaritem/visibilitypriority "When a
 * toolbar doesn't have enough space to fit all its items, it pushes items into the overflow
 * menu to make space. ... When two or more items share the same priority, the toolbar pushes
 * the one closest to the trailing edge first." The kit ships no overflow master (the whole
 * extraction has zero hits for "overflow"), so the CONSTRUCTION is the document's and the
 * numbers are the bar's own: the space it has, the space its parts ask for.
 *
 * WHAT IS MEASURED. `need` is what the bar's parts ask for with nothing squashed and the
 * window title given back - each part's own width, or its scroll width where it is already
 * being squeezed, minus whatever the heading currently occupies. What is left over is
 * `slack`. A negative slack means the heading is gone and a control is next, so one more
 * item goes to the menu; an item comes back only when
 * the slack is a WHOLE item wide, which is what keeps a bar sitting at exactly the wrong
 * width from flickering between the two answers: taking the item back makes the slack
 * smaller by the same amount that brought it back, never by more.
 */
function useToolbarOverflow(count: number): {
  readonly visible: number;
  readonly ref: Ref<HTMLFieldSetElement>;
} {
  const group = useRef<HTMLFieldSetElement>(null);
  const [visible, setVisible] = useState(count);

  useLayoutEffect(() => {
    setVisible(count);
  }, [count]);

  useLayoutEffect(() => {
    const node = group.current;
    const header = node?.closest('.shell-page-header');
    const row = node?.closest('.shell-toolbar-actions');
    if (!(header instanceof HTMLElement) || !(row instanceof HTMLElement)) return;

    const title = header.querySelector('.shell-window-title');
    const gapOf = (element: HTMLElement): number =>
      Number.parseFloat(getComputedStyle(element).columnGap) || 0;
    const widthOf = (element: HTMLElement): number =>
      Math.max(element.getBoundingClientRect().width, element.scrollWidth);
    /**
     * A slot asks for its own width, or for what its parts add up to - whichever is more,
     * MINUS the window title. The title is the elastic part of this bar (index.css gives it
     * a flex base of 0 and lets it grow), so counting what it currently occupies would make
     * the bar's need equal its room at every width and the measurement would say the same
     * thing for ever: at 1440 it read 1096 against 1091 and pushed an item into the menu on
     * five pixels of rounding. What the bar NEEDS is what is left when the heading has given
     * back everything it can.
     */
    const askedFor = (element: HTMLElement): number => {
      const parts = [...element.children].filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
      );
      const elastic =
        title !== null && element.contains(title) ? title.getBoundingClientRect().width : 0;
      const inside =
        parts.reduce((sum, part) => sum + (part === title ? 0 : widthOf(part)), 0) +
        gapOf(element) * Math.max(parts.length - 1, 0);
      return Math.max(widthOf(element) - elastic, inside);
    };

    const measure = (): void => {
      /* ONLY WHERE THE BAR IS ONE ROW. Below 64rem the header still wraps (index.css), and
         a bar with a second row has somewhere to put what does not fit: pushing an item
         into a menu there would hide a control that is on the screen. */
      if (getComputedStyle(header).flexWrap !== 'nowrap') {
        if (visible !== count) setVisible(count);
        return;
      }
      const slots = [...header.children].filter(
        (child): child is HTMLElement =>
          child instanceof HTMLElement && !child.classList.contains('shell-toolbar-edge'),
      );
      const need =
        slots.reduce((sum, slot) => sum + askedFor(slot), 0) +
        gapOf(header) * Math.max(slots.length - 1, 0);
      const room = header.clientWidth;
      const slack = room - need;
      const step =
        (Number.parseFloat(getComputedStyle(header).getPropertyValue('--shell-toolbar-item')) ||
          0) + gapOf(row);
      if (slack < 0 && visible > 0) setVisible(visible - 1);
      /* An item comes back only when the bar has room for a WHOLE one, which is the
         hysteresis: after it returns the slack is smaller by exactly that item, so the next
         measurement cannot want it gone again. */ else if (slack >= step && visible < count)
        setVisible(visible + 1);
    };

    measure();
    const observer = new ResizeObserver(() => measure());
    observer.observe(header);
    return () => observer.disconnect();
    /* ON THE SET, ON THE ANSWER, AND ON THE WINDOW - not on every render. The walk reads
       `getComputedStyle` on every part of the bar, which is cheap beside a paint and not
       beside a test's render; what can change the answer is the actions a screen hands in,
       the answer this hook last gave, and the width of the bar itself, and the observer
       covers the last one. */
  }, [count, visible]);

  return { visible, ref: group };
}

export function ToolbarActions({ actions }: { readonly actions: ReactNode }) {
  const items = flatten(actions);
  const prominent = items.find((item) => isProminent(item));
  /**
   * A SEGMENTED CONTROL IS ITS OWN GROUP, never an item inside one (SH-A1, owner-17 "Das
   * sind scheisse aus hier ist was faul"). The kit ships it as a container of its own -
   * `Titlebars and Toolbars/Light/XL/Segmented Control/Active/4 Items/1 Selected` is
   * 145x36 with its own padding and its own buttons - so sweeping it into the page-actions
   * capsule nested one capsule inside another and left the control's 28px segments hanging
   * four pixels past the 28px box the outer group had squeezed it into. That is the same
   * mistake round 3 recorded for the day control (finding A6), one composition further in.
   */
  const groups = items.filter(
    (item) =>
      isValidElement(item) && (item.type === ToolbarGroup || toolbarItemKind(item) === 'segmented'),
  );
  const secondary = items.filter((item) => item !== prominent && !groups.includes(item));
  const converted = secondary.map((item, index) => asToolbarSymbolItem(item, String(index)));
  /**
   * THE ONE PROMINENT ACTION KEEPS ITS WORD WHEN IT HAS NO SYMBOL (wave 4 round 1, ledger
   * S1-W4-3). Everything else with no symbol goes into the menu, because a bar item that
   * cannot be drawn is not a bar item - but the screen's PRINCIPAL verb is the one case
   * Apple states in words rather than leaving to the kit's symbol masters: "Use the
   * .prominent style for key actions such as Done or Submit ... Only specify one primary
   * action, and put it on the trailing side" (hig:
   * https://developer.apple.com/design/human-interface-guidelines/toolbars), and Done and
   * Submit are WORDS. Burying "Activate" or an import wizard's "Import 12 rows" behind an
   * ellipsis would hide the only thing those two screens exist to do.
   *
   * What C2-2 is actually about is the TINT, and the tint is gone from this slot either
   * way (`index.css`, `.shell-toolbar-prominent`); what is left is the kit's over-glass
   * text master wave 3 round 2 already sized this item with
   * (`Buttons/Light/Over-glass/Bordered/4 Lg`, `--shell-toolbar-text-inset`). The symbol
   * each of these pages should pass is in this round's report all the same.
   */
  function promote(item: ReactNode): ToolbarSymbolItem {
    const symbol = asToolbarSymbolItem(item, 'prominent');
    return symbol.kind === 'item' ? symbol : { kind: 'item', node: item };
  }
  const promoted = prominent === undefined ? undefined : promote(prominent);
  /**
   * AND THE BAR PUSHES ITS OWN ITEMS INTO THE SAME MENU WHEN THE WINDOW IS TOO NARROW
   * (wave 5, A2-3). `pushable` is the set of items that can be written as a menu row at all,
   * in page order; the overflow takes them from the TRAILING end, which is the order
   * `NSToolbarItem.VisibilityPriority` states for items of equal priority. An item with no
   * menu form - a control a page composed, a menu's own trigger - stays in the bar.
   */
  const pushable = converted.flatMap((entry, index) =>
    entry.kind === 'item' && entry.menu !== undefined ? [index] : [],
  );
  const { visible, ref: groupRef } = useToolbarOverflow(pushable.length);
  const pushed = new Set(pushable.slice(visible));
  const drawn = converted.flatMap((entry, index) =>
    entry.kind === 'item' && !pushed.has(index) ? [entry.node] : [],
  );
  const overflow = converted.flatMap((entry, index) => {
    if (entry.kind === 'overflow') return [entry];
    if (entry.kind === 'item' && entry.menu !== undefined && pushed.has(index)) return [entry.menu];
    return [];
  });
  /**
   * THE BAR'S OWN MORE MENU (wave 4 round 1, C2-2). An action with no symbol has nothing a
   * 28px monochrome slot can draw, and the kit ships no labelled toolbar button to fall
   * back on, so the honest place for it is the overflow the HIG already describes for a bar
   * that cannot show everything. It is the bar's item, not a page's: the pages hand in the
   * same verbs they always did, and each one leaves the menu again the moment its own file
   * passes a symbol. `Titlebars and Toolbars/Light/XL/Buttons/Active/1` with the ellipsis
   * is the same item every other action in the group is.
   */
  const more =
    overflow.length === 0 ? null : (
      <DropdownMenu
        key="more"
        trigger={
          <IconButton icon="more-horizontal" label="Show more actions" data-toolbar-item="more" />
        }
      >
        {overflow.map((entry) => (
          <DropdownItem
            key={entry.label}
            disabled={entry.disabled}
            onSelect={() => entry.onSelect()}
          >
            {entry.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    );
  return (
    <>
      {groups}
      {(drawn.length > 0 || more !== null) && (
        <ToolbarGroup label="Page actions" items={converted.length} ref={groupRef}>
          {drawn.map((node, index) => asToolbarItem(node, String(index)))}
          {more}
        </ToolbarGroup>
      )}
      {/*
        THE TRAILING ITEM IS A ONE-BUTTON GROUP, WHICH IS THE MASTER IT IS (wave 4 round 1,
        C2-2). kit: `Apple macOS 27 UI Kit.sketch / Titlebars and Toolbars/Light/XL/Buttons/Active/1`, a 36px group holding one 28px button.
        kit: `Apple macOS 27 UI Kit.sketch / Titlebars and Toolbars/Dark/XL/Buttons/Active/1`, the same 36px group and 28px button.
        This is the same construction as the four-button group with
        three buttons taken out - so the item takes the group's material instead of a rule
        of its own. It stood bare the moment the tint came off, because the glass this slot
        used to paint was the tint's, and the rim, the lens and the padding all belong to
        the group in `ui/glass.css` and `index.css` already.
      */}
      {promoted?.kind === 'item' && (
        <div className="shell-toolbar-prominent">
          <ToolbarGroup label="Primary action">
            {asToolbarItem(promoted.node, 'prominent')}
          </ToolbarGroup>
        </div>
      )}
    </>
  );
}

/**
 * THE PHONE'S BAR DRAWS THE SAME ITEMS (wave 5 round 3, C2-2; owner, 2026-09-17: "Auf dem
 * Handy steht Add und im Browser ist nur ein +").
 *
 * One control, two constructions: `PageHeader` handed the desktop's actions to
 * `ToolbarActions`, which turns a `Button` carrying a symbol into the kit's symbol item, and
 * rendered the SAME element verbatim below the breakpoint - so Members read "+ Add" on the
 * phone and a bare plus on the Mac, and so did Imports, Objects and fields and every record
 * list.
 *
 * APPLE HAS ONE RULE FOR BOTH BARS. The navigation-bars page is a 301 to this one
 * (verified 2026-09-17), which is the HIG saying they are one subject - "In iOS, a
 * navigation-specific toolbar is sometimes called a navigation bar" - and the rule it states
 * is "Make sure the meaning of each control is clear. Don't make people guess or experiment
 * to figure out what a toolbar item does. Prefer simple, recognizable symbols for items
 * instead of text, except for actions like edit that aren't well-represented by symbols"
 * (hig: https://developer.apple.com/design/human-interface-guidelines/toolbars). A plus is
 * the symbol for adding, which is also the owner's own reading of Apple in wave 4 (owner-92,
 * quoted at `app/workflows/WorkflowAdminPage.tsx`), and the two screens that already carried
 * their create action as an `IconButton` - Workflows and Reports - are the precedent this
 * generalises rather than a second opinion.
 *
 * AND ONLY THE SYMBOL CONVERSION, not the bar. The overflow measurement, the groups and the
 * More menu are the Mac window frame's; a phone bar that cannot draw an item has nowhere to
 * push it, so an action with no symbol keeps its word here - which is the sentence's own
 * exception, and is why this maps rather than reusing `ToolbarActions`.
 */
export function PhoneBarActions({ actions }: { readonly actions: ReactNode }) {
  return <BarActions actions={actions} />;
}

/**
 * THE SAME CONVERSION ON A ROW THAT IS NOT THE WINDOW FRAME'S (wave 5 round 5, C6-6; owner
 * 2026-09-17 on the record preview: "New Note... als Textpille neben Open").
 *
 * A DETAIL PANE'S FIRST LINE IS THAT PANE'S BAR. It sits on the same top edge as the list's
 * header row, it holds the pane's actions and nothing else, and `spec/screens/list.md`
 * section 2 item 9 puts it there for exactly that reason - so the sentence the window
 * toolbar obeys is the sentence it obeys: "Prefer simple, recognizable symbols for items
 * instead of text, except for actions like edit that aren't well-represented by symbols"
 * (hig: https://developer.apple.com/design/human-interface-guidelines/toolbars). Until this
 * round the rule reached a row only by being INSIDE `ToolbarActions` or `PhoneBarActions`,
 * which is why one "New Note…" was a plus-shaped symbol in the record's own bar and a text
 * pill in the pane beside it - one control, two constructions, which is the defect C2-2
 * already named on Members.
 *
 * WHAT IT IS NOT. The overflow measurement, the groups and the More menu stay the window
 * frame's: a pane row has nowhere to push an item, so - exactly as on the phone - an action
 * with no symbol keeps its word, which is the same sentence's own exception and is why
 * `Open`, `Next` and `Set <stage>` are still words here.
 *
 * `className` is the ROW's, because the row already has one: `.list-detail-actions` pushes
 * Open and the note apart and `.tasks-pane-actions` sets the queue pane's gap. A wrapper of
 * its own inside those would have made the row one flex item and undone both.
 */
export function BarActions({
  actions,
  className = 'flex flex-wrap items-center gap-2',
}: {
  readonly actions: ReactNode;
  readonly className?: string | undefined;
}) {
  return (
    <div className={className}>
      {flatten(actions).map((item, index) => {
        const entry = asToolbarSymbolItem(item, String(index));
        return entry.kind === 'item' ? entry.node : item;
      })}
    </div>
  );
}

/**
 * WHAT A TOOLBAR MAY CONTAIN (SH-A7, owner-52 "Was das denn" at the Enabled switch,
 * owner-56 at Delete beside it, owner-46 at an underlined link inside a capsule).
 *
 * Apple's list is closed and short: "The title of the current view", "Navigation controls,
 * like back and forward" and "Actions, or bar items"
 * (https://developer.apple.com/design/human-interface-guidelines/toolbars), and switches
 * are ruled out by name - "Use switches, checkboxes, and radio buttons in the window body,
 * not the window frame. In particular, avoid using these components in a toolbar or status
 * bar" (https://developer.apple.com/design/human-interface-guidelines/toggles). The kit
 * agrees by omission: there is no switch master and no link master in any toolbar group.
 *
 * SO THE FIVE KINDS ARE THESE, and the map is by COMPONENT rather than by prop, because a
 * screen hands `PageHeader` its own elements and nothing about a prop list would stop the
 * next screen from handing in a switch. A search field is the header's own (it renders it
 * from the `search` prop), and a spacer is layout rather than a control.
 */
export type ToolbarItemKind = 'button' | 'menu' | 'segmented' | 'search' | 'spacer';

const ITEM_KINDS: readonly (readonly [unknown, ToolbarItemKind])[] = [
  [Button, 'button'],
  [IconButton, 'button'],
  [ToolbarUndo, 'button'],
  [DropdownMenu, 'menu'],
  [SegmentedControl, 'segmented'],
];

export function toolbarItemKind(item: ReactNode): ToolbarItemKind | undefined {
  if (!isValidElement(item)) return undefined;
  return ITEM_KINDS.find(([component]) => item.type === component)?.[1];
}

/**
 * EVERYTHING ELSE IS RENDERED AND NAMED (the parent's instruction for tonight). Two
 * settings pages - the webhook detail and the access-role list - still hand in a switch, a
 * destructive action and a link; they are Parent C's to move into the pane body, and
 * removing them here tonight would blank a control a person uses. So a foreign item keeps
 * rendering and carries `data-toolbar-foreign` with the component's name, which is what
 * turns "no page control in the toolbar" into a check the running product can answer
 * (`packages/e2e/review/behaviour.spec.ts`, (i)) instead of a rule in a document.
 */
function asToolbarItem(item: ReactNode, key: string): ReactNode {
  if (toolbarItemKind(item) !== undefined) return item;
  if (!isValidElement(item)) return item;
  const type = item.type;
  const name =
    typeof type === 'string'
      ? type
      : ((type as { displayName?: string; name?: string }).displayName ??
        (type as { name?: string }).name ??
        'unknown');
  return (
    <span key={key} data-toolbar-foreign={name} className="contents">
      {item}
    </span>
  );
}

/**
 * THE PROMINENT ACTION IS THE ONE THE PAGE MARKED, WRAPPED OR NOT (SH-2, S1 round 5).
 *
 * Round 3 found the prominent action by asking whether the item IS a `Button` with
 * `variant="primary"`, which is true of Objects and fields' "New object" and false of
 * Members' "Add" - the same button, handed in as the TRIGGER of a `DropdownMenu` because
 * the pane has two things to create. So one settings pane showed a tinted capsule and the
 * next showed the same verb flat inside the neutral group, and nothing about the two panes
 * justified the difference.
 *
 * IT LOOKS THROUGH ONE WRAPPER, AND STRUCTURALLY. A wrapper that renders a trigger is
 * asked for its `trigger` prop rather than being compared to `DropdownMenu` by identity:
 * the check is then true of every control built that way (a popover, a split button) and
 * this file does not import a menu to know what a menu is. One level only - a trigger
 * inside a trigger is a construction nobody has designed, and it should be visible in
 * review rather than quietly promoted.
 *
 * A page can also say so outright: `prominent` on the element wins before either test, for
 * an action that is the screen's one verb without being a `primary` `Button`.
 */
function isProminent(item: ReactNode): boolean {
  if (!isValidElement<{ variant?: string; trigger?: ReactNode; prominent?: boolean }>(item))
    return false;
  if (item.props.prominent === true) return true;
  if (item.type === Button && item.props.variant === 'primary') return true;
  const trigger = item.props.trigger;
  return (
    isValidElement<{ variant?: string }>(trigger) &&
    trigger.type === Button &&
    trigger.props.variant === 'primary'
  );
}

/** Reused by the pushed record and the state gallery, with one bottom chrome row. */
export function PhoneToolbar({
  children,
  preview = false,
}: {
  readonly children: ReactNode;
  readonly preview?: boolean;
}) {
  return (
    <>
      {/* The same edge the tab bar guards (SH-A11): a pushed screen carries this bar
          instead of the tab bar, and its content runs beneath it in exactly the same way. */}
      {!preview && (
        <div className="shell-phone-chrome-edge">
          <ScrollEdge host="phone-toolbar" edge="bottom" />
        </div>
      )}
      <div
        role="toolbar"
        aria-label="Record toolbar"
        // The pushed screen's bottom bar floats the same way the tab bar does, and says so
        // once for the artifact (D4, wave 4 round 2 item 11).
        data-measure-ignore="covered-text:the scroll edge, and content passing UNDER this floating bar is the mechanism working; the content inset is what keeps the LAST line clear (docs/ios26-mechanisms.md)"
        className={cx('shell-phone-toolbar', preview && 'shell-phone-toolbar-preview')}
      >
        {children}
      </div>
    </>
  );
}
